import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import type { Channel, ChannelGroup } from "@/lib/chat-room/channels";

interface ChatChannelListProps {
  groups: ChannelGroup[];
  selectedKey: string | null;
  onSelect: (channel: Channel) => void;
  search: string;
  onSearch: (value: string) => void;
  avatarUrlFor: (userId: string | null | undefined) => string | null;
  isLoading: boolean;
}

const EMPTY_TEXT: Record<ChannelGroup["id"], string> = {
  prosjekter: "Ingen prosjekter der du er med i teamet.",
  huset: "Ingen kanaler for huset.",
  direkte: "Ingen samtaler ennå.",
};

/** Venstre kolonne i Chat-rommet: søk og kanaler gruppert i Prosjekter, Huset og Direkte. */
export function ChatChannelList({
  groups,
  selectedKey,
  onSelect,
  search,
  onSearch,
  avatarUrlFor,
  isLoading,
}: ChatChannelListProps) {
  return (
    <nav aria-label="Kanaler" className="flex h-full flex-col gap-4 overflow-y-auto bg-[hsl(var(--surface-light))] px-2.5 py-3.5">
      <label className="flex h-[38px] items-center gap-2 rounded-lg border-[1.5px] border-border bg-card px-2.5 text-muted-foreground">
        <SrIcon name="search" className="h-[15px] w-[15px]" />
        <span className="sr-only">Finn kanal eller person</span>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Finn kanal eller person"
          className="w-full border-0 bg-transparent text-[13px] text-foreground outline-none"
        />
      </label>

      {isLoading && (
        <div className="flex justify-center py-6">
          <SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading &&
        groups.map((group) => (
          <div key={group.id} className="flex flex-col gap-0.5">
            <h2 className="mb-1 ml-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {group.label}
            </h2>
            {group.channels.length === 0 ? (
              <p className="px-2.5 text-xs text-muted-foreground">{search ? "Ingen treff." : EMPTY_TEXT[group.id]}</p>
            ) : (
              group.channels.map((c) => (
                <ChannelRow
                  key={c.key}
                  channel={c}
                  active={c.key === selectedKey}
                  onSelect={() => onSelect(c)}
                  avatarUrl={avatarUrlFor(c.directUserId)}
                />
              ))
            )}
          </div>
        ))}
    </nav>
  );
}

function ChannelRow({
  channel,
  active,
  onSelect,
  avatarUrl,
}: {
  channel: Channel;
  active: boolean;
  onSelect: () => void;
  avatarUrl: string | null;
}) {
  const showUnread = channel.unread > 0 && !active;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? "true" : undefined}
      className={cn(
        "flex min-h-[40px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "bg-[var(--sr-revemoerk)] font-medium text-white"
          : cn("text-foreground hover:bg-secondary/60", showUnread ? "font-semibold" : "font-normal"),
      )}
    >
      <ChannelIcon channel={channel} active={active} avatarUrl={avatarUrl} />
      <span className="flex-1 truncate">{channel.title}</span>
      {channel.hasMention && !active && (
        <span
          aria-label="Du er nevnt"
          className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--sr-reverosa)] text-[11px] font-bold text-[var(--sr-revemoerk)]"
        >
          @
        </span>
      )}
      {showUnread && (
        <span
          aria-label={`${channel.unread} uleste`}
          className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground"
        >
          {channel.unread}
        </span>
      )}
    </button>
  );
}

function ChannelIcon({ channel, active, avatarUrl }: { channel: Channel; active: boolean; avatarUrl: string | null }) {
  if (channel.kind === "direct") {
    return <Avatar name={channel.title} avatarUrl={avatarUrl} size="sm" className="shrink-0" />;
  }
  const icon = channel.kind === "project" ? "folder" : channel.kind === "house" ? "megaphone" : "users";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
        active ? "bg-white/20 text-white" : "bg-secondary text-foreground",
      )}
    >
      <SrIcon name={icon} className="h-3.5 w-3.5" />
    </span>
  );
}
